import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { ApiResponse, IEquipment } from "../../../types";

export const equipmentApi = createApi({
  reducerPath: "equipmentApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Equipments"],

  endpoints: (builder) => ({
    getEquipment: builder.query<
      IEquipment[],
      { page?: number; limit?: number }
    >({
      query: ({ page, limit } = {}) => {
        const params = new URLSearchParams();
        if (page !== undefined) params.append("page", String(page));
        if (limit !== undefined) params.append("limit", String(limit));
        const queryString = params.toString();

        return {
          url: queryString
            ? `${ApiEndpoints.getAllEquipments}?${queryString}`
            : `${ApiEndpoints.getAllEquipments}`,
          method: "GET",
        };
      },
      transformResponse: (res: ApiResponse<IEquipment[]>) => {
        return res?.result ?? res?.results ?? [];
      },
      providesTags: ["Equipments"],
    }),

    getEquipmentById: builder.query<ApiResponse<IEquipment>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.equipment}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Equipments", id }],
    }),

    createEquipment: builder.mutation<
      ApiResponse<IEquipment>,
      Partial<IEquipment>
    >({
      query: (data) => ({
        url: ApiEndpoints.createEquipment,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Equipments"],
    }),

    editEquipment: builder.mutation<
      ApiResponse<IEquipment>,
      { id: string; data: Partial<IEquipment> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editEquipment}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Equipments",
        { type: "Equipments", id },
      ],
    }),

    deleteEquipment: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteEquipment}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Equipments"],
    }),
  }),
});

export const {
  useGetEquipmentQuery,
  useGetEquipmentByIdQuery,
  useCreateEquipmentMutation,
  useEditEquipmentMutation,
  useDeleteEquipmentMutation,
} = equipmentApi;
